// 📞 보이스톡(1:1 음성 통화) — 사이드카 시그널링(/api/tpt/call/*) + [TPT_CALL]{json} 마커 헬퍼.
// 아키텍처: 벨울림은 DM 에 [TPT_CALL] invite 마커 메시지(기존 DDP rooms-changed 로 실시간 도달),
//   SDP(offer/answer) 교환은 mongo tpt_calls 문서 + 벨울림 동안만 1s 폴링. 연결 후 제어(음소거/화면공유/종료)는
//   RTCDataChannel — 서버 부하 없음. ICE 는 non-trickle(수집 완료 후 SDP 1회 교환) — 사내 LAN host candidate 전제.

export const CALL_MARKER = '[TPT_CALL]';

/** 통화 로그 마커 — 발신자가 invite 로 게시 후, 종료 시 같은 메시지를 최종 상태로 수정(방에 카드 1개 유지). */
export interface CallCardPayload {
	kind: 'call';
	callId: string;
	state: 'invite' | 'ended' | 'missed' | 'declined';
	from: string;          // 발신자 username
	to: string;            // 수신자 username
	fromName?: string;
	dur?: number;          // ended 일 때 통화시간(초)
	/** voice=보이스톡(기본) / screen=화면만 공유(음성 없음, 2026-08-06 사용자 — "전화 거는 게 아니라 화면만").
	 *  roomshare=방 화면공유(발표자 1명 → 시청자 N명, 2026-08-12) — 벨울림 없이 방에 참여 카드만 남긴다.
	 *  서버(tpt_calls)는 mode 를 모름 — 마커로만 전달(수신측 CallDock 이 벨울림 UI/미디어 구성 분기). */
	mode?: 'voice' | 'screen' | 'roomshare';
	/** roomshare 전용 — 참여에 필요한 공유 세션 id (tpt_room_shares.shareId) */
	shareId?: string;
}

export function parseCallCard(msg: string): CallCardPayload | null {
	if (!msg || !msg.startsWith(CALL_MARKER)) return null;
	try {
		const p = JSON.parse(msg.slice(CALL_MARKER.length)) as CallCardPayload;
		if (p && p.kind === 'call' && typeof p.callId === 'string' && p.from && p.to) return p;
	} catch { /* not a call marker */ }
	return null;
}

export function buildCallMarker(p: CallCardPayload): string {
	return CALL_MARKER + JSON.stringify(p);
}

/** mm:ss (1시간 넘으면 h:mm:ss) */
export function fmtCallDur(sec: number): string {
	const s = Math.max(0, Math.floor(sec));
	const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), ss = s % 60;
	const p = (n: number) => String(n).padStart(2, '0');
	return h > 0 ? `${h}:${p(m)}:${p(ss)}` : `${m}:${p(ss)}`;
}

// ── 사이드카 REST ────────────────────────────────────────────────────────────

export interface CallStateDoc {
	callId: string;
	rid: string;
	state: 'ringing' | 'accepted' | 'declined' | 'ended' | 'cancelled';
	from: { userId: string; username: string; name?: string };
	to: { username: string };
	offer?: string;
	answer?: string;
	reason?: string;       // declined: 'busy' 등
	// 🔁 통화 중 재연결(ICE 재시작, 2026-10-06) — 발신자 reoffer / 수신자 reanswer, reSeq 로 회차 일치
	reoffer?: string;
	reanswer?: string;
	reSeq?: number;
}

function hdr(token: string, userId: string): Record<string, string> {
	return { 'Content-Type': 'application/json', 'X-Auth-Token': token, 'X-User-Id': userId };
}

async function post<T>(path: string, token: string, userId: string, body: Record<string, unknown>): Promise<T> {
	const r = await fetch(`/api/tpt/call/${path}`, { method: 'POST', headers: hdr(token, userId), body: JSON.stringify(body) });
	const j = await r.json().catch(() => ({}));
	if (!r.ok || j.error) throw new Error(j.error || `call/${path} ${r.status}`);
	return j as T;
}

/** mode/fromName — 서버가 FCM 즉시 벨 발사에 사용 (screen 은 벨 아님 → 서버가 미발사, 2026-08-07 #4). */
export function callStart(token: string, userId: string, rid: string, to: string, opts?: { mode?: 'voice' | 'screen'; fromName?: string }): Promise<{ callId: string; busy?: boolean }> {
	return post('start', token, userId, { rid, to, ...(opts?.mode ? { mode: opts.mode } : {}), ...(opts?.fromName ? { fromName: opts.fromName } : {}) });
}

export function callSignal(token: string, userId: string, callId: string, type: 'offer' | 'answer' | 'reoffer' | 'reanswer', sdp: string, seq?: number): Promise<{ ok: boolean; seq?: number }> {
	return post('signal', token, userId, { callId, type, sdp, ...(seq ? { seq } : {}) });
}

export async function callState(token: string, userId: string, callId: string): Promise<CallStateDoc | null> {
	const r = await fetch(`/api/tpt/call/state?callId=${encodeURIComponent(callId)}`, { headers: hdr(token, userId) });
	if (!r.ok) return null;
	const j = await r.json().catch(() => null);
	return j && j.callId ? (j as CallStateDoc) : null;
}

export function callAccept(token: string, userId: string, callId: string): Promise<{ ok: boolean }> {
	return post('accept', token, userId, { callId });
}

export function callDecline(token: string, userId: string, callId: string, reason?: string): Promise<{ ok: boolean }> {
	return post('decline', token, userId, { callId, ...(reason ? { reason } : {}) });
}

export function callEnd(token: string, userId: string, callId: string): Promise<{ ok: boolean }> {
	return post('end', token, userId, { callId });
}

// 📊 통화 기록 (2026-10-13 형 "보이스톡 끊김 — 권고대로") — 통화가 끝날 때 양쪽이 각자 한 건씩 보낸다(서버 tpt_call_log, 90일).
//   종전엔 끊긴 이유가 어디에도 남지 않아 "1분 안팎 끊김"을 마커 길이로만 추정했다. keepalive — 창을 닫으며 끝나도 전송되게.
//   실패는 무시(통화 종료에 영향 없음).
export interface CallReport {
	callId: string; role: 'caller' | 'callee'; mode: 'voice' | 'screen';
	reason: 'local' | 'remote' | 'declined' | 'missed'; detail?: string; dur: number; restarts: number;
	platform: 'android' | 'ios' | 'desktop' | 'web'; appVer: string; phase?: string;
}
export function callReport(token: string, userId: string, r: CallReport): void {
	try {
		void fetch('/api/tpt/call/report', { method: 'POST', headers: hdr(token, userId), body: JSON.stringify(r), keepalive: true }).catch(() => undefined);
	} catch { /* 무시 */ }
}

/** ICE 서버 설정 — 서버 .env TPT_CALL_STUN_URLS(쉼표구분) 기반. 미설정=빈 배열(LAN host candidate 만). */
export async function callIceConfig(): Promise<RTCIceServer[]> {
	try {
		const r = await fetch('/api/tpt/call/config');
		if (!r.ok) return [];
		const j = await r.json().catch(() => ({}));
		return Array.isArray(j.iceServers) ? (j.iceServers as RTCIceServer[]) : [];
	} catch { return []; }
}
